import { QuestState } from "../types/quests/indext";

export type QuestReward = { icon?: string; name?: string; quantity?: number; level?: number };

type ListedQuest = {
  state: number;
  progress?: number;
  quest?: { eventQuestKey?: string; requiredActionValue?: number; rewards?: string } | null;
};

// Event quests (eventQuestKey, e.g. the arena tour's) are followed by their own
// screens, not the quest list. The list and its "ready to claim" badges both
// leave them out, so a badge never counts a quest the list does not show.
export function isListedQuest(item: ListedQuest | null | undefined): boolean {
  return Boolean(item?.quest) && !item?.quest?.eventQuestKey;
}

// How far a quest is: the server's progress against its target, clamped. A quest
// that is done is complete whatever progress it reports.
export function questProgress(item: ListedQuest) {
  const target = Math.max(1, Number(item.quest?.requiredActionValue) || 1);
  const done = item.state === QuestState.Done || item.state === QuestState.Claimed;
  const current = done ? target : Math.min(target, Math.max(0, Number(item.progress) || 0));
  return { current, target, ratio: current / target };
}

const stateOrder: Record<number, number> = {
  [QuestState.Done]: 0,
  [QuestState.Open]: 1,
  [QuestState.Claimed]: 2,
  [QuestState.Expired]: 3,
};

// Ready to claim first, then open quests closest to done, then the rest; ties
// keep the server's order.
export function orderQuests<T extends ListedQuest>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const byState = (stateOrder[a.item.state] ?? 4) - (stateOrder[b.item.state] ?? 4);
      if (byState) return byState;
      if (a.item.state === QuestState.Open) {
        const byProgress = questProgress(b.item).ratio - questProgress(a.item).ratio;
        if (byProgress) return byProgress;
      }
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

// The quest's rewards as the server describes them (icon, name, quantity, level).
// A malformed list shows no rewards rather than breaking the quest list.
export function parseQuestRewards(rewards: string | null | undefined): QuestReward[] {
  try {
    const parsed = JSON.parse(rewards || "[]");
    return Array.isArray(parsed) ? parsed.filter((reward) => reward && typeof reward === "object") : [];
  } catch {
    return [];
  }
}

// 950, 12.5K, 3M: short enough for a reward slot.
export function compactNumber(value: number): string {
  const number = Number(value) || 0;
  if (Math.abs(number) < 1000) return number.toLocaleString();
  try {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(number);
  } catch {
    return number.toLocaleString();
  }
}
